"use client";

import { useMemo } from "react";
import { FgInput } from "@/components/fg-input";
import { FgToggle } from "@/components/fg-toggle";
import { AiAssistButton } from "../AiAssistButton";
import { FormValidityBanner } from "../FormValidityBanner";
import { RubricEditor, type RubricCriterion } from "../RubricEditor";
import { MobilePreview } from "../MobilePreview";

export interface FreeWritingBuilderProps {
  /** The essay prompt shown to the learner. */
  prompt: string;
  onPromptChange: (next: string) => void;
  /** Minimum word count. */
  minWords: number;
  onMinWordsChange: (next: number) => void;
  /** Maximum word count. */
  maxWords: number;
  onMaxWordsChange: (next: number) => void;
  /** Hard cap on character count (rejects longer submissions server-side). */
  maxLength: number;
  onMaxLengthChange: (next: number) => void;
  /** Difficulty 1-5. */
  difficulty: number;
  onDifficultyChange: (next: number) => void;
  /** AI grading on/off. */
  aiGradingEnabled: boolean;
  onAiGradingEnabledChange: (next: boolean) => void;
  rubric: RubricCriterion[];
  onRubricChange: (next: RubricCriterion[]) => void;
  level: "A1" | "A2" | "B1" | "B2" | "C1";
}

/**
 * Long-form writing exercise builder. Differs from FreeAnswerBuilder in:
 *   - No reference answer (writing is judged entirely by the rubric)
 *   - Adds a word-count band (minWords..maxWords)
 *   - Hard character cap (server rejects longer submissions)
 */
export function FreeWritingBuilder({
  prompt,
  onPromptChange,
  minWords,
  onMinWordsChange,
  maxWords,
  onMaxWordsChange,
  maxLength,
  onMaxLengthChange,
  difficulty,
  onDifficultyChange,
  aiGradingEnabled,
  onAiGradingEnabledChange,
  rubric,
  onRubricChange,
  level,
}: FreeWritingBuilderProps) {
  const validity = useMemo(
    () => [
      {
        label: "Prompt written",
        severity: prompt.trim() ? ("ok" as const) : ("error" as const),
      },
      {
        label: "Word-count band valid",
        severity:
          minWords > 0 && maxWords > minWords
            ? ("ok" as const)
            : ("error" as const),
      },
      {
        label: "Rubric weights sum to 100",
        severity:
          rubric.reduce((s, r) => s + r.weight, 0) === 100
            ? ("ok" as const)
            : ("warn" as const),
      },
    ],
    [prompt, minWords, maxWords, rubric],
  );

  return (
    <div className="flex flex-col gap-4">
      <FormValidityBanner title="Free writing" checks={validity} />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.6fr,1fr]">
        <div className="flex flex-col gap-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
              Essay prompt
            </label>
            <textarea
              value={prompt}
              onChange={(e) => onPromptChange(e.target.value)}
              rows={4}
              placeholder="Write a short paragraph (80–120 words) describing your morning routine in German."
              className="w-full rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#141414] p-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:border-[#FFD60A] focus:outline-none"
            />
            <div className="mt-1 flex items-center justify-between text-[10px] font-mono text-[#8E8E93]">
              <span>Aim for {minWords}–{maxWords} words.</span>
              <span>{prompt.trim() ? prompt.trim().split(/\s+/).length : 0} words typed</span>
            </div>
          </div>
          {prompt.trim() && (
            <AiAssistButton
              kind="essay_prompt"
              prompt={`Improve / rewrite this German essay prompt at CEFR ${level}: ${prompt}`}
              level={level}
              label="✨ Polish prompt"
              onApply={(content) => {
                if (typeof content === "string" && content.trim()) {
                  onPromptChange(content.trim());
                }
              }}
            />
          )}
        </div>
        <MobilePreview kind="free_writing" prompt={prompt} />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <FgInput
          label="Min words"
          type="number"
          value={String(minWords)}
          onChange={(e) => onMinWordsChange(Number(e.target.value) || 0)}
          min={0}
        />
        <FgInput
          label="Max words"
          type="number"
          value={String(maxWords)}
          onChange={(e) => onMaxWordsChange(Number(e.target.value) || 1)}
          min={1}
        />
        <FgInput
          label="Hard char cap"
          type="number"
          value={String(maxLength)}
          onChange={(e) => onMaxLengthChange(Number(e.target.value) || 1000)}
          min={100}
          max={50000}
        />
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
            Difficulty
          </label>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => onDifficultyChange(d)}
                className={
                  "h-9 w-9 rounded-md border text-sm font-bold transition-colors " +
                  (difficulty >= d
                    ? "bg-[#FFD60A] border-[#FFD60A] text-[#0A0A0A]"
                    : "bg-[#141414] border-[rgba(255,255,255,0.08)] text-[#8E8E93] hover:text-[#FAFAFA]")
                }
                aria-label={`Difficulty ${d}`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      </div>

      <FgToggle
        label="Grade with Gemini"
        checked={aiGradingEnabled}
        onChange={onAiGradingEnabledChange}
      />

      {aiGradingEnabled && (
        <RubricEditor
          value={rubric}
          onChange={onRubricChange}
          promptHint={prompt}
          level={level}
        />
      )}
    </div>
  );
}